import { RepoType, CodeRepoReviewStatus } from '@amzn/awscat-pattern-library-service-model';
import {
  Alert,
  Box,
  Button,
  Grid,
  FormField,
  Header,
  Input,
  Link,
  Modal,
  Select,
  SpaceBetween,
} from '@amzn/awsui-components-core';
import UserIdAutosuggest from '../common/UserIdAutosuggest';
import { CodeRepoTabViewModel, ValidationResult } from './CodeRepoTabViewModel';

const REVIEW_STATUS_OPTIONS = [
  { value: CodeRepoReviewStatus.NotStarted, label: 'Not Started' },
  { value: CodeRepoReviewStatus.InProgress, label: 'In Progress' },
  { value: CodeRepoReviewStatus.ResolvedSuccessful, label: 'Resolved Successfully' },
  { value: CodeRepoReviewStatus.ResolvedUnsuccessful, label: 'Resolved Unsuccessfully' },
];

function getValidationResultErrorMessage(result: ValidationResult): string | undefined {
  return result.valid ? undefined : result.message;
}

function OptionalLabel({ text }: { readonly text: string }) {
  return (
    <span>
      {text} <i>- optional</i>
    </span>
  );
}

function SecurityReviewDescription() {
  return (
    <>
      <Link
        href="https://taskei.amazon.dev/tasks/create?template=c806913f-810b-4e85-a84e-437a6243cfd4"
        external
        variant="info"
      >
        Click here
      </Link>{' '}
      to create a ticket for the security review process and link it below. This ticket will be
      assigned to you and includes required steps and directions. You should log all of your results
      to this ticket. When you are finished,{' '}
      <Link
        href="https://talos.security.aws.a2z.com/#/talos/guardian-finder"
        external
        variant="info"
      >
        click here
      </Link>{' '}
      to find a Security Guardian who will review your steps and results.
    </>
  );
}

export function CodeRepoModalView({ vm }: { readonly vm: CodeRepoTabViewModel }) {
  const modal = vm.modal;
  if (modal.kind === 'closed') return null;

  const { fields } = modal;
  const header = modal.kind === 'adding' ? 'Add repository' : 'Edit repository';

  return (
    <Modal
      visible
      size="large"
      header={header}
      onDismiss={vm.dismissModal}
      closeAriaLabel="Close modal"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={vm.dismissModal}>
              Cancel
            </Button>
            <Button variant="primary" disabled={!fields.isSaveEnabled} onClick={vm.saveModal}>
              Save
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="l">
        <Box variant="p">
          {vm.repoType === RepoType.Internal
            ? 'Accepted code repos are code.amazon.com, code.aws.dev, gitlab.aws.dev, or an official AWS repo (such as aws-samples) on github.com (not your own private repo). AWS Prescriptive Guidance follows the process for all repos.'
            : 'Accepted code repos are official AWS repos (such as aws-samples) on github.com (not your own private repo). AWS Prescriptive Guidance follows the process for all repos.'}
        </Box>

        <SpaceBetween size="m">
          <Grid gridDefinition={[{ colspan: 6 }, { colspan: 3 }, { colspan: 3 }]}>
            <FormField
              label="Repository URL"
              constraintText={
                vm.repoType === RepoType.Internal
                  ? 'Accepted URLs must start with https:// and use an approved domain (code.amazon.com, code.aws.dev, gitlab.aws.dev, or github.com). No trailing slash or special characters (& ?).'
                  : 'Accepted URLs must start with https:// and use an approved GitHub organization (e.g. aws-samples, awslabs, aws). No trailing slash or special characters (& ?).'
              }
              errorText={getValidationResultErrorMessage(fields.errors.url)}
            >
              <Input
                value={fields.url}
                placeholder={
                  vm.repoType === RepoType.Internal
                    ? 'https://code.amazon.com/packages/myRepo'
                    : 'https://github.com/aws-samples/myRepo'
                }
                onChange={({ detail }) => vm.changeUrl(detail.value)}
              />
            </FormField>
            <FormField
              label={<OptionalLabel text="Branch" />}
              errorText={getValidationResultErrorMessage(fields.errors.branch)}
            >
              <Input
                value={fields.branch}
                placeholder="main"
                onChange={({ detail }) => vm.changeBranch(detail.value)}
              />
            </FormField>
            <FormField
              label={<OptionalLabel text="Label" />}
              errorText={getValidationResultErrorMessage(fields.errors.label)}
            >
              <Input
                value={fields.label}
                placeholder="My repo"
                onChange={({ detail }) => vm.changeLabel(detail.value)}
              />
            </FormField>
          </Grid>

          {vm.repoType === RepoType.Internal && (
            <Alert type="info">
              External sharing of content marked as Internal is strictly restricted to
              customers/partners under SOW only.
            </Alert>
          )}
        </SpaceBetween>

        <SpaceBetween size="m">
          <Header variant="h3">Security review</Header>
          <Grid gridDefinition={[{ colspan: 6 }, { colspan: 3 }, { colspan: 3 }]}>
            <FormField
              label={<OptionalLabel text="Review URL" />}
              constraintText={<SecurityReviewDescription />}
              errorText={getValidationResultErrorMessage(fields.errors.appsecTicket)}
            >
              <Input
                value={fields.appsecTicket}
                placeholder="https://sim.amazon.com/..."
                onChange={({ detail }) => vm.changeAppsecTicket(detail.value)}
              />
            </FormField>
            <FormField label="Status">
              <Select
                selectedOption={
                  REVIEW_STATUS_OPTIONS.find((o) => o.value === fields.appsecStatus) ?? null
                }
                options={REVIEW_STATUS_OPTIONS}
                onChange={({ detail }) =>
                  vm.changeAppsecStatus(detail.selectedOption.value as CodeRepoReviewStatus)
                }
              />
            </FormField>
            <FormField
              label={<OptionalLabel text="Guardian" />}
              constraintText="Once a Security Guardian has reviewed your code repo, select their alias here"
            >
              <UserIdAutosuggest
                userId={fields.appsecGuardian || undefined}
                isRequired={false}
                onUserSelected={(selectedUserId: string | undefined) => {
                  vm.changeAppsecGuardian(selectedUserId ?? '');
                }}
              />
            </FormField>
          </Grid>
        </SpaceBetween>

        {vm.isExternalAudience && (
          <SpaceBetween size="m">
            <Header variant="h3">Open source review</Header>
            <Grid gridDefinition={[{ colspan: 9 }, { colspan: 3 }]}>
              <FormField
                label={<OptionalLabel text="Review URL" />}
                constraintText={
                  <>
                    <Link
                      href="https://w.amazon.com/bin/view/Open_Source/Open_Sourcing/"
                      external
                      variant="info"
                    >
                      Click here
                    </Link>{' '}
                    to create a ticket for the open source process and copy the ticket link below.
                    When it is completed, update the status.
                  </>
                }
                errorText={getValidationResultErrorMessage(fields.errors.openSourceTicket)}
              >
                <Input
                  value={fields.openSourceTicket}
                  placeholder="https://sim.amazon.com/..."
                  onChange={({ detail }) => vm.changeOpenSourceTicket(detail.value)}
                />
              </FormField>
              <FormField label="Status">
                <Select
                  selectedOption={
                    REVIEW_STATUS_OPTIONS.find((o) => o.value === fields.openSourceStatus) ?? null
                  }
                  options={REVIEW_STATUS_OPTIONS}
                  onChange={({ detail }) =>
                    vm.changeOpenSourceStatus(detail.selectedOption.value as CodeRepoReviewStatus)
                  }
                />
              </FormField>
            </Grid>
          </SpaceBetween>
        )}
      </SpaceBetween>
    </Modal>
  );
}
